import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  createTemplate,
  listTemplates,
  deleteTemplate,
  updateTemplate,
} from "../../../api/routes";
import * as client from "../../../client";

export type TemplateUseCase =
  client.AsgardCreateTemplateAsgardCreateTemplatePostData["useCase"];

export type ParsedTemplate = Omit<client.Template, "created_at_ts"> & {
  created_at_ts: Date;
};

export const useTemplates = (
  user: client.User | undefined,
  useCase: TemplateUseCase
): UseQueryResult<ParsedTemplate[]> => {
  return useQuery({
    enabled: !!user?.is_client_admin,
    queryKey: ["list-templates"],
    queryFn: async () => await listTemplates(),
    select: (templates) =>
      templates.templates
        .filter((t) => t.use_case === useCase)
        .map((t) => ({ ...t, created_at_ts: new Date(t.created_at_ts + "Z") })),
  });
};

export const useAddTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-template"],
    mutationFn: async ({
      template,
      useCase,
    }: {
      template: File;
      useCase: TemplateUseCase;
    }) => await createTemplate(template, useCase),
    onSuccess: (data) => {
      queryClient.setQueryData(["list-templates"], data);
    },
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-template"],
    mutationFn: async (requestBody: client.DeleteTemplate) =>
      await deleteTemplate(requestBody),
    onSuccess: (data) => {
      queryClient.setQueryData(["list-templates"], data);
    },
  });
};
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-template"],
    mutationFn: async (requestBody: client.UpdateTemplate) =>
      await updateTemplate(requestBody),
    onSuccess: (data) => {
      queryClient.setQueryData(["list-templates"], data);
    },
  });
};
